import { useTranslation } from 'react-i18next';
import { Alert } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useWindowTitle } from '@/shared/windowing';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { useGetInventoryVehicle } from '@/api/generated/inventory-vehicles/inventory-vehicles';
import { vehicleDetailAgenda } from './vehicleDetailAgenda';
import { VehicleDetailView } from './VehicleDetailView';
import { emptyVehicleFormValues, fromGetVehicleResponse } from './vehicleFormSchema';

interface VehicleDetailPageProps {
  layerId: string;
}

export function VehicleDetailPage({ layerId }: VehicleDetailPageProps) {
  const { t } = useTranslation('inventory');
  const { open } = useAgendaParams(vehicleDetailAgenda, layerId);
  const vehicleId = open.vehicleId;
  const isEdit = vehicleId != null;

  // Krátký staleTime (jednotky sekund) — detail se při každém otevření načte znovu, cache jen pro
  // rychlé překlikávání. Po uložení navíc detail invalidujeme (viz VehicleDetailView.onSaved).
  const vehicleQuery = useGetInventoryVehicle(vehicleId ?? 0, {
    query: { enabled: isEdit, staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });

  useWindowTitle(vehicleQuery.data?.name ?? undefined);

  if (isEdit && vehicleQuery.isLoading) {
    return <p className="text-phx-text-muted text-phx-sm p-phx-5">{t('vehicle.loading')}</p>;
  }
  if (isEdit && vehicleQuery.isError) {
    return (
      <div className="p-phx-5">
        <Alert severity="error" title={t('vehicle.loadErrorTitle')}>
          {t('vehicle.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <VehicleDetailView
      layerId={layerId}
      vehicleId={vehicleId}
      vehicle={vehicleQuery.data}
      initialValues={
        vehicleQuery.data ? fromGetVehicleResponse(vehicleQuery.data) : emptyVehicleFormValues()
      }
    />
  );
}
